import { createFileRoute } from '@tanstack/react-router'
import provinces from '@/constants/provinces.json'
import cities from '@/constants/cities.json'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import {useMutation} from '@tanstack/react-query'
import {createProfile} from '@/queries/profile'
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from '@/components/ui/card'
import {Label} from '@/components/ui/label'
import {Button, buttonVariants} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Combobox} from '@/components/ui/combobox'
import DatePicker from '@/components/ui/date-picker' // Assuming shadcn date picker is in this path
import client from '@/services/api'
import userStore from '@/stores/user-store'
import { toast } from 'sonner'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { useState } from 'react'

export const Route = createFileRoute('/profile/complete/')({
  component: RouteComponent,
})

function RouteComponent() {
  return <CompleteProfile />
}

const baseSchema = z.object({
  name: z.string()
    .regex(/^[\u0600-\u06FF0-9_\-\s]+$/gm, 'نام و نام خانوادگی باید به زبان فارسی نوشته شود')
    .min(1, 'وارد کردن نام و نام خانوادگی الزامی است'),
  province: z.string().min(1, 'وارد کردن استان الزامی می باشد'),
  city: z.string().min(1, 'وارد کردن شهر الزامی می باشد'),
  address: z.string()
    .regex(/^[\u0600-\u06FF0-9_\-\s]+$/gm, 'آدرس پستی باید به زبان فارسی نوشته شود')
    .min(1, 'وارد کردن آدرس الزامی می باشد'),
  postal_code: z.string()
    .regex(/(?!(\d)\1{3})[13-9]{4}[1346-9][013-9]{5}/gm, 'کد پستی وارد شده صحیح نمی باشد')
    .min(1, 'وارد کردن کد پستی ضروری است'),
  national_code: z.string()
    .min(10, 'کد ملی باید حداقل 10 رقم باشد')
    .max(10, 'کد ملی نمی تواند بیشتر از 10 رقم باشد')
    .min(1, 'وارد کردن کد ملی الزامی است'),
  store_name: z.string()
    .max(70, 'نام فروشگاه نمی تواند بیشتر از ۷۰ کارکتر باشد')
    .min(1, 'وارد کردن نام فروشگاه الزامی است'),
  gender: z.string().min(1, 'انتخاب یک جنسیت ضروری می باشد'),
  birth_date: z.date().optional(),
  profession: z.string().optional(),
  education: z.string().optional(),
  phone: z.string().optional(),
  type: z.string(),
})

const personalSchema = baseSchema.extend({
  type: z.literal('personal'),
  legal_info: z.undefined().optional(), // نیازی نیست
})

const companySchema = baseSchema.extend({
  type: z.literal('company'),
  legal_info: z.object({
    company_name: z.string().min(1, 'نام شرکت الزامی است'),
    company_code: z.string().min(1, 'شناسه شرکت الزامی است'),
    economic_code: z.string().min(1, 'کد اقتصادی الزامی است'),
    // هرچی خواستی اضافه کن
  }),
})

export const validationSchema = z.discriminatedUnion('type', [
  personalSchema,
  companySchema,
])

type ValidationSchema = z.infer<typeof validationSchema>

const CompleteProfile = () => {
  const navigate = Route.useNavigate()
  const [type, setType] = useState('')

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors, isValid, isSubmitting },
  } = useForm<ValidationSchema>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      name:'',
      province: '',
      city: '',
      address: '',
      postal_code: '',
      birth_date: undefined,
      profession: '',
      education: '',
      store_name: '',
      phone: '',
      national_code: '',
      gender: '',
      type: 'personal',
    },
    mode: 'onChange',
  })

  const provinceValue = provinces.filter(p => p.name === watch('province'))?.[0]?.id

  const profileCreateMuation = useMutation({
    mutationFn: (variable: Object) => createProfile(variable),
    mutationKey: ['createProfile'],
    onSuccess: () => {
      toast.success('پروفایل با موفقیت ثبت شد')
      client
        .get('/auth/check')
        .then(({data}: any) => {
          userStore.setState(prev => ({...prev, user: data.user, setting: data.setting.settings}))
        })
      navigate({to:'/'})
    },
    onError: () => {
      toast.error('خطایی در ثبت اطلاعات رخ داده است')
    },
  })

  const onSubmit = (values: ValidationSchema) => {
    let data = {...values} as any
    const newData = Object.fromEntries(Object.entries(data).filter(([_, v]) => v !== '' && v !== undefined))

    if (typeof newData.legal_info === 'object') {
      newData.legal_info = JSON.stringify(newData.legal_info)
    }

    profileCreateMuation.mutate(newData)
  }

  console.log(errors)

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>تکمیل پروفایل</CardTitle>
          <CardDescription>
            بعد از تایید پروفایل شما توسط تیم ایساج شما دسترسی پنل و خرید شارژ خواهید داشت
          </CardDescription>
        </CardHeader>

        <CardContent>
          <RadioGroup onValueChange={(type: 'personal' | 'company') => {
            if (type === 'personal') {
              setValue('legal_info', undefined)
            }
            setValue('type', type)
          }} value={watch('type')} className='flex gap-2 mb-4'>
            <Label htmlFor='personal' className={buttonVariants({variant:watch('type') === 'personal' ? 'default' : 'outline', className: 'w-52'})}>
            <RadioGroupItem value='personal' id="personal" className='hidden'></RadioGroupItem>
            حقیقی
            </Label>
            <Label htmlFor='company' className={buttonVariants({variant:watch('type') === 'company' ? 'default' : 'outline', className: 'w-52'})}>
            <RadioGroupItem value='company' id="company" className='hidden'></RadioGroupItem>
              حقوقی
            </Label>
          </RadioGroup>
          <form onSubmit={handleSubmit(onSubmit)} className='grid grid-cols-2 gap-4'>
            {watch('type') == 'company' && <>
            
              <div className="mb-3">
                <Label>نام شرکت</Label>
                <Input {...register('legal_info.company_name')}/>
                <span className='text-xs text-red-500'>{errors.legal_info !== undefined && errors.legal_info?.company_name?.message}</span>
              </div>

              <div className="mb-3">
                <Label>کد اقتصادی</Label>
                <Input {...register('legal_info.company_code')}/>
                <span className='text-xs text-red-500'>{errors.legal_info !== undefined && errors.legal_info?.company_code?.message}</span>
              </div>
              <div className="mb-3">
                <Label>شناسه ملی</Label>
                <Input {...register('legal_info.economic_code')}/>
                <span className='text-xs text-red-500'>{errors.legal_info !== undefined && errors.legal_info?.economic_code?.message}</span>
              </div>
            </>}
            <div className="mb-3">
              <Label>
                نام و نام خانوادگی
              </Label>
              <Input {...register('name')} />
              <span className='text-xs text-red-500'>{errors.name?.message}</span>
            </div>
            <div className='mb-3'>
              <Label>استان *</Label>
              <Combobox
                placeholder='استان خود را انتخاب کنید...'
                emptyMessage='استان مورد نظر یافت نشد'
                options={provinces.map((p: any) => ({value: p.name, label: p.name}))}
                value={watch('province')}
                onSelect={(p: any) => setValue('province', p)}
              />
              <span className='text-xs text-red-500'>{errors.province?.message}</span>
            </div>
            <div className='mb-3'>
              <Label>شهر *</Label>
              <Combobox
                placeholder='شهر خود را انتخاب کنید'
                emptyMessage='شهر مورد نظر یافت نشد'
                options={cities
                  .filter((p: any) => p.province_id === provinceValue)
                  .map((p: any) => ({value: p.name, label: p.name}))}
                value={watch('city')}
                onSelect={(v: any) => {
                  console.log(v)
                  setValue('city', v)}}
              />
              <span className='text-xs text-red-500'>{errors.city?.message}</span>
            </div>
            <div className='flex flex-col gap-2'>
              <Label>آدرس</Label>
              <Input {...register('address')} />
              <span className='text-xs text-red-500'>{errors.address?.message}</span>
            </div>

            <div className='flex flex-col gap-2'>
              <Label>کد پستی</Label>
              <Input {...register('postal_code')} />
              <span className='text-xs text-red-500'>{errors.postal_code?.message}</span>
            </div>

            <div className='flex flex-col gap-2'>
              <Label>نام فروشگاه</Label>
              <Input {...register('store_name')} />
              <span className='text-xs text-red-500'>{errors.store_name?.message}</span>
            </div>

            <div className='flex flex-col gap-2'>
              <Label>کد ملی</Label>
              <Input {...register('national_code')} />
              <span className='text-xs text-red-500'>{errors.national_code?.message}</span>
            </div>

            <div className='mb-3 flex flex-col gap-2'>
              <Label>جنسیت *</Label>
              <Combobox
                options={[
                  {label: 'آقا', value: 'male'},
                  {label: 'خانم', value: 'female'},
                ]}
                value={watch('gender')}
                onSelect={(e) => setValue('gender', e)}
              />
              <span className='text-xs text-red-500'>{errors.gender?.message}</span>
            </div>

            <div className='flex flex-col gap-2'>
              <Controller
                control={control}
                name="birth_date"
                render={({ field }) => (
                  <DatePicker
                    value={field.value}
                    onChange={field.onChange}
                    label='تاریخ تولد'
                  />
                )}
              />
              <span className='text-xs text-red-500'>{errors.birth_date?.message}</span>
            </div>

            <div className='flex flex-col gap-2'>
              <Label>شغل</Label>
              <Input {...register('profession')} />
              <span className='text-xs text-red-500'>{errors.profession?.message}</span>
            </div>

            <div className='flex flex-col gap-2'>
              <Label>تحصیلات</Label>
              <Input {...register('education')} />
              <span className='text-xs text-red-500'>{errors.education?.message}</span>
            </div>

            <div className='flex flex-col gap-2 lg:col-span-2'>
              <Label>شماره تلفن ثابت</Label>
              <Input {...register('phone')} />
              <span className='text-xs text-red-500'>{errors.phone?.message}</span>
            </div>

            

            <div className='sm:col-span-2'>
              <Button
                color='btn-primary'
                type='submit'
                isLoading={profileCreateMuation.isPending || isSubmitting}
                disabled={profileCreateMuation.isPending || isSubmitting}
              >
                ثبت اطلاعات
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </>
  )
}
